/*
 * assets/defer-section.js
 *
 * IO-triggered hydrator for below-the-fold home sections. Generalises the shipped
 * DeferredLoading (theme.dev.js:1018) from a mouseenter trigger to an IntersectionObserver
 * (mirrors aosAnchorObserver, theme.dev.js:2068), hydrates inside requestIdleCallback
 * (polyfill theme.dev.js:2430), then re-inits carousels by dispatching the theme's OWN
 * shopify:section:load event (theme.dev.js:478 -> load() -> component constructors ->
 * new Flickity). Standalone module (NOT a patch to theme.dev.js, which is auto-synced from
 * Shopify and would be clobbered).
 *
 * AOS: `.aos-initialized [data-aos^=fade] { opacity: 0.001 }` (theme.css:6678) hides
 * animated elements until `.aos-animate` is added. Content hydrated AFTER load lives under
 * an already-`.aos-initialized` root, so we force `.aos-animate` on hydration to guarantee
 * visibility (the below-fold entry animation is cosmetic and dropped by design).
 *
 * Rollback: the kill switch is the Liquid setting `settings.enable_section_defer`
 * (renders inline, no <deferred-section> emitted, this module is a no-op). Do NOT roll back
 * by removing this include while the wrapper is on — that would strand templated sections.
 */
(function () {
  'use strict';

  var ROOT_MARGIN = '600px 0px'; // hydrate ~1 screen early so content is ready on arrival
  var idle =
    window.requestIdleCallback ||
    function (cb) { return window.setTimeout(cb, 1); };

  function revealAos(root) {
    // Force AOS end-state so deferred [data-aos] content is never left invisible.
    var nodes = root.querySelectorAll('[data-aos]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('aos-animate');
    }
  }

  var DeferredSection = (function () {
    function DeferredSection() {
      return Reflect.construct(HTMLElement, [], DeferredSection);
    }
    DeferredSection.prototype = Object.create(HTMLElement.prototype);
    DeferredSection.prototype.constructor = DeferredSection;

    DeferredSection.prototype.connectedCallback = function () {
      if (this._observing) return;
      this._observing = true;

      var self = this;
      this._io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            self._io.unobserve(self);
            self._io.disconnect();
            idle(function () { self.hydrate(); });
            break;
          }
        },
        { rootMargin: ROOT_MARGIN }
      );
      this._io.observe(this);
    };

    DeferredSection.prototype.hydrate = function () {
      if (this.dataset.hydrated) return;
      this.dataset.hydrated = '1';

      var tpl = this.querySelector('template[data-defer-body]');
      if (!tpl) return;

      var frag = tpl.content.cloneNode(true);
      var sectionEl = frag.querySelector('[data-section-id]');
      var sectionId = sectionEl
        ? sectionEl.getAttribute('data-section-id')
        : this.getAttribute('data-section-id');

      var skeleton = this.querySelector('.defer-skeleton');
      if (skeleton) skeleton.remove();

      this.appendChild(frag);
      tpl.remove();

      // Guarantee visibility for AOS-animated content (see header note).
      revealAos(this);

      // Hand off to theme-sections' own boot path -> constructors -> new Flickity.
      // Handler (theme.dev.js:478) reads event.target + detail.sectionId, then load().
      var root = this.closest('.shopify-section') || this;
      root.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: sectionId }
        })
      );
      // Keep the reserved min-height: it is a floor, never a cap, so it cannot clip real
      // content and avoids any post-hydrate reflow.
    };

    return DeferredSection;
  })();

  if ('customElements' in window && !customElements.get('deferred-section')) {
    customElements.define('deferred-section', DeferredSection);
  }
})();
